import { useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { AffiliateOrder, AffiliateOrderImportResult } from "@glow-price/shared";
import { formatPriceCNY } from "@glow-price/shared";

import { affiliateNetworksApi, affiliateOrdersApi } from "../api/resources";

const STATUS_OPTIONS = [
  { value: "pending", label: "pending" },
  { value: "confirmed", label: "confirmed" },
  { value: "settled", label: "settled" },
  { value: "refunded", label: "refunded" },
  { value: "cancelled", label: "cancelled" },
];

const TEMPLATE_HEADER = "external_order_id,click_id,network_code,order_amount,commission_amount,order_status,ordered_at,settled_at";

export function AffiliateOrdersPage() {
  const { message } = App.useApp();
  const [networkId, setNetworkId] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [importResult, setImportResult] = useState<AffiliateOrderImportResult | null>(null);

  const networksQuery = useQuery({
    queryKey: ["affiliate-networks", "all"],
    queryFn: () => affiliateNetworksApi.list({ page: 1, pageSize: 200 }),
  });
  const networkOptions = (networksQuery.data?.items ?? []).map((n) => ({
    value: n.id,
    label: `${n.name} (${n.code})`,
  }));

  const list = useQuery({
    queryKey: ["affiliate-orders", networkId, status, page],
    queryFn: () => affiliateOrdersApi.list({ networkId, status, page, pageSize: 30 }),
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => affiliateOrdersApi.importCsv(file),
    onSuccess: (data) => {
      setImportResult(data);
      message.success(`导入完成 新增 ${data.inserted} / 更新 ${data.updated} / 跳过 ${data.skipped}`);
      list.refetch();
    },
    onError: (err: Error) => message.error(err.message),
  });

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: "space-between", width: "100%" }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          联盟订单
        </Typography.Title>
        <Space>
          <Select
            allowClear
            placeholder="联盟"
            options={networkOptions}
            value={networkId}
            onChange={(v) => {
              setPage(1);
              setNetworkId(v);
            }}
            style={{ width: 200 }}
          />
          <Select
            allowClear
            placeholder="状态"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
            style={{ width: 140 }}
          />
          <Upload
            accept=".csv"
            showUploadList={false}
            beforeUpload={(file) => {
              importMutation.mutate(file as File);
              return false;
            }}
          >
            <Button loading={importMutation.isPending} type="primary">
              导入 CSV
            </Button>
          </Upload>
        </Space>
      </Space>

      <Card size="small" style={{ marginBottom: 16 }} title="CSV 列说明">
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          首行为表头，列顺序不强制，列名需匹配：
        </Typography.Paragraph>
        <Typography.Text code copyable>
          {TEMPLATE_HEADER}
        </Typography.Text>
      </Card>

      <Table<AffiliateOrder>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        pagination={{
          current: page,
          pageSize: 30,
          total: list.data?.total ?? 0,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        columns={[
          {
            title: "下单时间",
            dataIndex: "orderedAt",
            width: 140,
            render: (v) => dayjs(v).format("YYYY-MM-DD HH:mm"),
          },
          { title: "外部订单号", dataIndex: "externalOrderId", width: 200 },
          {
            title: "联盟",
            dataIndex: "networkId",
            width: 160,
            render: (v) => networkOptions.find((o) => o.value === v)?.label ?? v,
          },
          { title: "click_id", dataIndex: "clickId", width: 200, render: (v) => v ?? "—" },
          {
            title: "订单金额",
            dataIndex: "orderAmount",
            width: 120,
            render: (v) => formatPriceCNY(v),
          },
          {
            title: "佣金",
            dataIndex: "commissionAmount",
            width: 120,
            render: (v) => formatPriceCNY(v),
          },
          {
            title: "状态",
            dataIndex: "orderStatus",
            width: 110,
            render: (v) => {
              const color =
                v === "settled" ? "green" : v === "refunded" || v === "cancelled" ? "red" : "default";
              return <Tag color={color}>{v}</Tag>;
            },
          },
          {
            title: "结算时间",
            dataIndex: "settledAt",
            width: 140,
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "—"),
          },
        ]}
      />

      <Modal
        open={!!importResult}
        title="导入结果"
        onCancel={() => setImportResult(null)}
        onOk={() => setImportResult(null)}
      >
        {importResult && (
          <>
            <Typography.Paragraph>
              共 {importResult.totalRows} 行 / 新增 {importResult.inserted} / 更新{" "}
              {importResult.updated} / 跳过 {importResult.skipped}
            </Typography.Paragraph>
            {importResult.errors.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${importResult.errors.length} 条错误`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {importResult.errors.slice(0, 30).map((e, i) => (
                      <li key={i}>
                        行 {e.row}: {e.reason}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
